import { createAction } from '@reduxjs/toolkit'
import { createReducer, createTypes } from 'reduxsauce'
import fetchApi from '../utils/fetch'

//
// ACTIONS
// -----------------------------------------------------------------------------

export const types = createTypes(`
  VERIFICATION_REQUESTED
  VERIFICATION_CLEAR
  VERIFICATION_FETCH_SUCCESS
  VERIFICATION_FETCH_FAILURE
`)

export const verificationStatus = {
  idle: 'idle',
  loading: 'loading',
  verified: 'verified',
  notFound: 'not_found',
  revoked: 'revoked',
  throttled: 'throttled',
  error: 'error'
}

//
// REDUCER
// -----------------------------------------------------------------------------

export const INITIAL_STATE = {
  status: verificationStatus.idle,
  // The code being (or last) looked up, in the form it arrived from the URL
  code: null,
  // The public payload ({ type: 'course' | 'general', name, … }) once verified
  data: null
}

// Backend contract (SPEC-040): 404 unknown/unclaimed, 410 revoked with a
// machine-readable body, 429 throttled. Revocation is read from the body as
// well as the status so the page never relies on the status code alone.
export const statusFromFailure = (payload = {}) => {
  const { status, value } = payload || {}
  if (status === 410 || value?.revoked === true)
    return verificationStatus.revoked
  if (status === 404) return verificationStatus.notFound
  if (status === 429) return verificationStatus.throttled
  return verificationStatus.error
}

export const requested = (_, action) => ({
  ...INITIAL_STATE,
  status: verificationStatus.loading,
  code: action.payload
})

export const clear = () => INITIAL_STATE

// Completions carry the code they were requested for (`meta.code`). Only the
// lookup the store is currently tracking may resolve: a response that lands
// after the visitor started another code, or after the page cleared the slice
// on leave, would otherwise overwrite the newer state or resurrect a result.
const isCurrent = (state, meta) =>
  Boolean(meta) && state.code !== null && meta.code === state.code

export const success = (state = INITIAL_STATE, { payload, meta }) => {
  if (!isCurrent(state, meta)) return state
  // D4: the machine-readable flag wins even on a 2xx body
  if (payload?.revoked === true)
    return {
      ...state,
      status: statusFromFailure({ value: payload }),
      data: null
    }
  return { ...state, status: verificationStatus.verified, data: payload }
}

export const failure = (state = INITIAL_STATE, { payload, meta }) => {
  if (!isCurrent(state, meta)) return state
  return { ...state, status: statusFromFailure(payload), data: null }
}

export const HANDLERS = {
  [types.VERIFICATION_REQUESTED]: requested,
  [types.VERIFICATION_CLEAR]: clear,
  [types.VERIFICATION_FETCH_SUCCESS]: success,
  [types.VERIFICATION_FETCH_FAILURE]: failure
}

export default createReducer(INITIAL_STATE, HANDLERS)

//
// ACTION CREATORS
// -----------------------------------------------------------------------------

export const verificationRequested = createAction(types.VERIFICATION_REQUESTED)
export const verificationClear = createAction(types.VERIFICATION_CLEAR)
// Completions are stamped with the code they answer (see `isCurrent`)
const withCode = (payload, code) => ({ payload, meta: { code } })
export const verificationFetchSuccess = createAction(
  types.VERIFICATION_FETCH_SUCCESS,
  withCode
)
export const verificationFetchFailure = createAction(
  types.VERIFICATION_FETCH_FAILURE,
  withCode
)

// Public lookup: no JWT is sent when logged out and none is needed. `hidden`
// keeps the global top loading bar out of it — the verify card shows its own
// in-flight state, and the bar renders even on nav-less pages.
export const verificationFetch = (code) => {
  if (typeof code === 'undefined')
    throw TypeError('first param cannot be undefined')
  return fetchApi(
    `/verify/${encodeURIComponent(code)}/`,
    { method: 'GET', hidden: true },
    (payload) => verificationFetchSuccess(payload, code),
    (payload) => verificationFetchFailure(payload, code)
  )
}
